/**
 * Shared column definitions for the patient information report exports
 * (Excel and CSV). The column order follows the registration page.
 */

export interface PatientExportHelpers {
    t: (key: string) => string
    /** whether the clinic uses SN codes; when false the SN column is omitted */
    sn: boolean
    /** formats a date value for display (DD/MM/YYYY) */
    formatDate: (value: any) => string
}

export interface PatientExportColumn {
    /** translation key for the header cell */
    header: string
    value: (item: any, index: number) => string | number
}

export interface EmergencyContact {
    fullname?: string
    relation?: string
    phone?: string
}

export interface PatientExportTable {
    headers: string[]
    rows: Array<Array<string | number>>
}

const DASH = '-'

const orDash = (value: any): string | number => (value === null || value === undefined || value === '' ? DASH : value)

export const parseEmergencyContacts = (parents: unknown): EmergencyContact[] => {
    if (Array.isArray(parents)) return parents
    if (typeof parents === 'string') {
        try {
            const parsed = JSON.parse(parents)
            return Array.isArray(parsed) ? parsed : []
        } catch (e) {
            return []
        }
    }
    return []
}

const joinContacts = (item: any, pick: (contact: EmergencyContact) => string | undefined): string => {
    const values = parseEmergencyContacts(item.parents)
        .map((contact) => (pick(contact) || '').trim())
        .filter((value) => value !== '')
    return values.length > 0 ? values.join('; ') : DASH
}

const trueFalse = (t: (key: string) => string, value: any, yesKey: string, noKey: string): string => {
    if (value === '1' || value === 1 || value === true) return t(yesKey)
    if (value === '0' || value === 0 || value === false) return t(noKey)
    return DASH
}

export const getPatientExportColumns = ({ t, sn, formatDate }: PatientExportHelpers): PatientExportColumn[] => {
    const columns: Array<PatientExportColumn | false> = [
        { header: 'REPORT.TABLE.PATIENT.#NO', value: (_item, index) => index + 1 },
        { header: 'REPORT.TABLE.PATIENT.PATIENT_CN_NUMBER', value: (item) => orDash(item.cnNumber) },
        { header: 'REPORT.TABLE.PATIENT.PATIENT_BRANCH_CN_NUMBER', value: (item) => orDash(item.branchCnNumber) },
        sn && { header: 'REPORT.TABLE.PATIENT.PATIENT_SN_CODE', value: (item) => orDash(item.snCode) },
        { header: 'REPORT.TABLE.PATIENT.PATIENT_NAME', value: (item) => `${item.prefixName || ''}${item.firstname || ''} ${item.lastname || ''}` },
        { header: 'REGISTER.FORM.FIRST_NAME', value: (item) => orDash(item.firstnameEn) },
        { header: 'REGISTER.FORM.LAST_NAME', value: (item) => orDash(item.lastnameEn) },
        { header: 'REGISTER.FORM.DISPLAY_NAME', value: (item) => orDash(item.displayName) },
        { header: 'REPORT.TABLE.PATIENT.ID_CARD', value: (item) => orDash(item.idCard) },
        { header: 'REGISTER.FORM.PASSPORT', value: (item) => orDash(item.passport) },
        { header: 'REGISTER.FORM.WORK_PERMIT_NUMBER', value: (item) => orDash(item.workPermitNumber) },
        { header: 'REPORT.TABLE.PATIENT.GENDER', value: (item) => (item.gender ? t(`REPORT.VALUE.PATIENT.GENDER.${item.gender}`) : DASH) },
        { header: 'REPORT.TABLE.PATIENT.DATE_OF_BIRTH', value: (item) => (item.dateOfBirth ? formatDate(item.dateOfBirth) : t('REPORT.VALUE.PATIENT.BLOOD_TYPE.NOT_SPECIFIED')) },
        { header: 'REPORT.TABLE.PATIENT.AGE', value: (item) => orDash(item.age) },
        // registration page: เชื้อชาติ = nationality, สัญชาติ = origin
        { header: 'REPORT.TABLE.PATIENT.ETHNICITY', value: (item) => orDash(item.nationalityName) },
        { header: 'REPORT.TABLE.PATIENT.NATIONALITY', value: (item) => orDash(item.originName) },
        { header: 'REPORT.TABLE.PATIENT.RELIGION', value: (item) => (item.religion ? t(`REGISTER.FORM.RELIGION_OPTION.${item.religion}`) : DASH) },
        { header: 'REPORT.TABLE.PATIENT.MARITAL', value: (item) => (item.maritalStatus ? t(`REGISTER.FORM.MARITAL_OPTION.${item.maritalStatus}`) : DASH) },
        { header: 'REPORT.TABLE.PATIENT.OCCUPATION', value: (item) => orDash(item.careerName) },
        { header: 'REPORT.TABLE.PATIENT.CAREER_LINE', value: (item) => orDash(item.careerLineName) },
        { header: 'REGISTER.FORM.SCHOOL', value: (item) => orDash(item.school) },
        { header: 'REPORT.TABLE.PATIENT.CHANNEL', value: (item) => orDash(item.accessChannel) },
        { header: 'REGISTER.FORM.ACCESS_CHANNEL_OTHER', value: (item) => orDash(item.accessChannelOther) },
        { header: 'REGISTER.FORM.SERVICE_FACTOR', value: (item) => orDash(item.serviceFactorName) },
        { header: 'REGISTER.FORM.SERVICE_FACTOR_OTHER', value: (item) => orDash(item.serviceFactorOther) },
        { header: 'REPORT.TABLE.PATIENT.MOBILE', value: (item) => orDash(item.phone) },
        { header: 'REPORT.TABLE.PATIENT.TEL', value: (item) => orDash(item.telephone) },
        { header: 'REPORT.TABLE.PATIENT.LINE', value: (item) => orDash(item.line) },
        { header: 'REPORT.TABLE.PATIENT.EMAIL', value: (item) => orDash(item.email) },
        { header: 'REPORT.TABLE.PATIENT.FACEBOOK', value: (item) => orDash(item.facebook) },
        { header: 'REPORT.TABLE.PATIENT.PATIENT_TYPE', value: (item) => orDash(item.patientTypeName) },
        { header: 'REPORT.TABLE.PATIENT.BLOOD_TYPE', value: (item) => (item.bloodType === 'NOT_SPECIFIED' ? t('REPORT.VALUE.PATIENT.BLOOD_TYPE.NOT_SPECIFIED') : orDash(item.bloodType)) },
        { header: 'REGISTER.FORM.FREQUENCY_CIGARETTE', value: (item) => orDash(item.frequencyCigarette) },
        { header: 'REGISTER.FORM.FREQUENCY_ALCOHOL', value: (item) => orDash(item.frequencyAlcohol) },
        { header: 'REGISTER.FORM.PREGNANT_HISTORY', value: (item) => (item.pregnantHistory ? t(item.pregnantHistory) : DASH) },
        { header: 'REGISTER.FORM.SURGERY_HISTORY', value: (item) => (item.surgeryHistory ? t(item.surgeryHistory) : DASH) },
        { header: 'REGISTER.FORM.SURGERY_DATE', value: (item) => (item.surgeryDate ? formatDate(item.surgeryDate) : DASH) },
        { header: 'REPORT.TABLE.PATIENT.DISEASE', value: (item) => orDash(item.disease) },
        { header: 'REPORT.TABLE.PATIENT.MEDICINE', value: (item) => orDash(item.medicineUse) },
        { header: 'REPORT.TABLE.PATIENT.ALLERGIC', value: (item) => orDash(item.beAllergic) },
        { header: 'REPORT.TABLE.PATIENT.SYMPTOMS', value: (item) => orDash(item.importantSymptoms) },
        { header: 'REGISTER.FORM.SYMPTOMS', value: (item) => orDash(item.symptoms) },
        { header: 'REGISTER.FORM.IS_PREGNANT', value: (item) => trueFalse(t, item.isPregnant, 'REGISTER.CHECKBOX.YES', 'REGISTER.CHECKBOX.NO') },
        { header: 'REGISTER.FORM.PREGNANT_MONTH', value: (item) => (item.pregnantMonth ? t(item.pregnantMonth) : DASH) },
        { header: 'REGISTER.FORM.IS_RECUPERATE', value: (item) => trueFalse(t, item.isRecuperate, 'REGISTER.CHECKBOX.HAVE_HISTORY', 'REGISTER.CHECKBOX.NOT_HAVE_HISTORY') },
        { header: 'REGISTER.FORM.IS_MEDICAL_CERTIFICATE', value: (item) => trueFalse(t, item.isMedicalCertificate, 'REGISTER.CHECKBOX.NEED_MEDICAL_CERTIFICATE', 'REGISTER.CHECKBOX.NOT_NEED_MEDICAL_CERTIFICATE') },
        { header: 'REGISTER.FORM.IS_INSURE', value: (item) => trueFalse(t, item.isInsure, 'REGISTER.CHECKBOX.NEED_INSURE', 'REGISTER.CHECKBOX.NOT_NEED_INSURE') },
        { header: 'REPORT.TABLE.PATIENT.TREATMENT_RIGHT', value: (item) => orDash(item.groupTreatmentRight) },
        { header: 'REGISTER.FORM.COUNTRY', value: (item) => orDash(item.countryName) },
        { header: 'REPORT.TABLE.PATIENT.ADDRESS', value: (item) => orDash(item.fullAddress) },
        { header: 'REGISTER.FORM.ADDRESS_DETAILS_EN', value: (item) => orDash(item.addressEn) },
        { header: 'REGISTER.FORM.POSTAL_CODE', value: (item) => orDash(item.postcode) },
        { header: 'REGISTER.TITLE.ADDRESS_AT_WORK', value: (item) => orDash(item.fullOfficeAddress) },
        { header: 'REGISTER.TITLE.PARENTS', value: (item) => joinContacts(item, (c) => c.fullname) },
        { header: 'REGISTER.FORM.RELATIONSHIP', value: (item) => joinContacts(item, (c) => c.relation) },
        { header: 'REGISTER.FORM.PHONE_NUMBER', value: (item) => joinContacts(item, (c) => c.phone) },
        { header: 'REGISTER.FORM.WEIGHT', value: (item) => orDash(item.weight) },
        { header: 'REGISTER.FORM.HEIGHT', value: (item) => orDash(item.height) },
        {
            header: 'REGISTER.FORM.SYSTOLIC_BLOOD_PRESSURE',
            value: (item) => {
                const hasSystolic = item.systolicBloodPressure !== null && item.systolicBloodPressure !== undefined && item.systolicBloodPressure !== ''
                const hasDiastolic = item.diastolicBloodPressure !== null && item.diastolicBloodPressure !== undefined && item.diastolicBloodPressure !== ''
                if (!hasSystolic && !hasDiastolic) return DASH
                return `${hasSystolic ? item.systolicBloodPressure : DASH}/${hasDiastolic ? item.diastolicBloodPressure : DASH}`
            },
        },
        { header: 'REGISTER.FORM.WAISTLINE', value: (item) => orDash(item.waistline) },
        { header: 'REGISTER.FORM.TEMPERATURE', value: (item) => orDash(item.temperature) },
        { header: 'REGISTER.FORM.PULSE_RATE', value: (item) => orDash(item.pulseRate) },
        { header: 'REGISTER.FORM.HEART_RATE', value: (item) => orDash(item.heartRate) },
        { header: 'REGISTER.FORM.BMI', value: (item) => orDash(item.bmi) },
        { header: 'REGISTER.FORM.BSA', value: (item) => orDash(item.bsa) },
    ]
    return columns.filter((column): column is PatientExportColumn => column !== false)
}

export const buildPatientExportTable = (data: any[], helpers: PatientExportHelpers): PatientExportTable => {
    const columns = getPatientExportColumns(helpers)
    return {
        headers: columns.map((column) => column.header),
        rows: data.map((item, index) => columns.map((column) => column.value(item, index))),
    }
}

const escapeCsvField = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return ''
    const text = String(value)
    if (/[",\r\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
    }
    return text
}

/** Serialises rows to CSV text with a UTF-8 BOM so Excel renders Thai correctly. */
export const toCsv = (rows: Array<Array<string | number | null | undefined>>): string => {
    return '﻿' + rows.map((row) => row.map(escapeCsvField).join(',')).join('\r\n')
}
